import ByLegCollector from '@components/Filters/Components/FiltersAccordion/queries/ByLegCollector';
import AirportCollector from '@components/Filters/Components/AirportFilter/AirportFilterQueries/AirportCollector';
import FeeTypeCollector from '@components/Shared/FeeSelector/data/FeeTypeCollector';
import CarrierCollector from '@components/Filters/Components/CarrierFilter/queries/CarrierCollector';
import DepartureTimeLegCollector from '@components/Filters/Components/TimeFilter/queries/DepartureTimeLegCollector';
import ConnectionThresholdsByTransportTypeAndLegCollector from '@components/Filters/Components/MaxStopsFilter/queries/Collector';
import ConnectionDurationByLegCollector from '@components/Filters/Components/StopDurationFilter/queries/Collector';
import TransportTypeCollector from '@components/Filters/Components/TransportTypeFilter/queries/Collector';
import ConnectionByLegCollector from '@components/Filters/Components/StopAirportFilter/queries/Collector';
import PriceCollector from '@components/Filters/Components/PriceFilter/queries/Collector';
import SemMonthFilterCollector from '@components/SEM/Filters/SemMonthFilter/semMonthFilterQueries/SemMonthFilterCollector';
import SemWeekDayFilterCollector from '@components/SEM/Filters/SemWeekDayFilter/semWeekDayFilterQueries/SemWeekDayFilterCollector';
import SemDurationFilterCollector from '../../../components/Filters/Components/DurationFilter/durationFilterQueries/semDurationFilterCollector';
import { PerformedSearchRequestContext } from '../SearchRequestProvider';
import {
  FeeType,
  SegmentFragment,
  ItineraryFragment,
  SearchQueryQuery,
} from 'generated/graphql';
import { BestItineraryData } from 'clientState/providers/FilteredItinerariesProvider/createBestItineraryData';
import AircraftCollector from '@components/Filters/Components/AircraftFilter/Collector/AircraftCollector';

const getLegLocations = (leg: { segments: SegmentFragment[] }) => {
  const { sections } = leg?.segments[0] || [];
  return {
    departure: sections[0].departure,
    destination: sections[sections.length - 1].destination,
  };
};

const getLocationsByLeg = ({ legs }: ItineraryFragment) =>
  legs?.map(getLegLocations);

const AvailableFiltersBuilder = (
  data: SearchQueryQuery,
  searchRequest: PerformedSearchRequestContext,
  isUnbundle = false
) => {
  const { search } = data || {};
  const { itineraries, defaultFeeType } = search || {};
  const [itinerary] = itineraries || [];
  const transportTypeCollector = TransportTypeCollector();
  const departureTimeCollector = ByLegCollector(DepartureTimeLegCollector);
  const connectionThresholdsByTransportTypeAndLegCollector =
    ConnectionThresholdsByTransportTypeAndLegCollector(searchRequest);
  const connectionDurationsByLegCollector = ConnectionDurationByLegCollector(
    searchRequest,
    isUnbundle
  );
  const connectionsCollector = ConnectionByLegCollector(
    searchRequest,
    isUnbundle
  );
  const airportCollector = AirportCollector(searchRequest, isUnbundle);
  const carrierCollector = CarrierCollector(searchRequest);
  const feeTypeCollector = FeeTypeCollector(defaultFeeType as FeeType);
  const priceCollector = PriceCollector();
  const aircraftModelCollector = AircraftCollector();
  let hasPrimeDayItineraries = false;
  let hasCarbonFootprintItineraries = false;
  const semMonthFilterCollector = SemMonthFilterCollector();
  const semWeekDayFilterCollector = SemWeekDayFilterCollector();
  const semDurationFilterCollector = SemDurationFilterCollector();

  return {
    add(itinerary: ItineraryFragment) {
      hasPrimeDayItineraries =
        hasPrimeDayItineraries ||
        (!!itinerary.campaignConfig?.primeDayConfig &&
          !!itinerary.campaignConfig?.primeDayConfig?.isPrimeDayFare);
      hasCarbonFootprintItineraries =
        hasCarbonFootprintItineraries ||
        (!!itinerary.carbonFootprint && !!itinerary.carbonFootprint?.isEco);
      semMonthFilterCollector.add(itinerary);
      semWeekDayFilterCollector.add(itinerary);
      semDurationFilterCollector.add(itinerary);
      itinerary.fees?.forEach(({ type }) => feeTypeCollector.add(type));
      itinerary.legs?.forEach((leg, legIndex) => {
        leg.segments.forEach((segment) => {
          const { sections } = segment;
          transportTypeCollector.add(segment);
          departureTimeCollector.add(legIndex, {
            itinerary,
            sections,
            defaultFeeType,
          });
          connectionThresholdsByTransportTypeAndLegCollector.add(
            legIndex,
            segment
          );
          connectionDurationsByLegCollector.add(legIndex, segment);
          connectionsCollector.add(legIndex, segment);
          airportCollector.add(legIndex, segment);
          carrierCollector.add(legIndex, segment);
          segment.sections.forEach(({ vehicleModel }) => {
            aircraftModelCollector.add(legIndex, vehicleModel);
          });
        });
      });
    },
    build(maxPrice?: BestItineraryData) {
      if (itineraries.length === 0) {
        return null;
      }
      return {
        locationsByLeg: getLocationsByLeg(itinerary),
        transportTypes: transportTypeCollector.build(),
        departureTimes: departureTimeCollector.build(),
        connectionThresholds:
          connectionThresholdsByTransportTypeAndLegCollector.build(),
        connectionDurations: connectionDurationsByLegCollector.build(),
        connections: connectionsCollector.build(),
        airports: airportCollector.build(),
        carriers: carrierCollector.build(),
        feeTypes: feeTypeCollector.build(),
        hasPrimeDayItineraries,
        hasCarbonFootprintItineraries,
        price: priceCollector.build(maxPrice),
        aircraftModels: aircraftModelCollector.build(),
        semMonths: semMonthFilterCollector.build(),
        semWeekDays: semWeekDayFilterCollector.build(),
        semDurationRange: semDurationFilterCollector.build(),
      };
    },
  };
};

export default AvailableFiltersBuilder;
